import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signInWithPopup, 
  GoogleAuthProvider, 
  sendPasswordResetEmail 
} from 'firebase/auth';
import { Mail, Lock, Phone, UserCheck, KeyRound, ArrowLeft, Eye, EyeOff, CheckCircle2, XCircle } from 'lucide-react';
import { db } from '../firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';

export default function Login() {
  const [isSignup, setIsSignup] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  
  // Form States
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mobile, setMobile] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // OTP States for Email Verification
  const [step, setStep] = useState('FORM'); // 'FORM' | 'OTP_VERIFY'
  const [emailOtp, setEmailOtp] = useState('');
  const [generatedOtp, setGeneratedOtp] = useState('');

  const [loading, setLoading] = useState(false);
  const auth = getAuth();
  const navigate = useNavigate();

  // Password validation checkers
  const hasMinLength = password.length >= 6;
  const hasUppercase = /[A-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecialChar = /[^A-Za-z0-9]/.test(password);

  // 1. Google Sign-In Handler
  const handleGoogleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    try {
      setLoading(true);
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) {
        await setDoc(userRef, {
          email: user.email,
          createdAt: new Date().toISOString(),
          role: 'customer'
        });
      }

      toast.success("Successfully logged in with Google!");
      navigate('/');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to trigger OTP generation
  const triggerOtpGeneration = () => {
    const mockOtp = Math.floor(100000 + Math.random() * 900000).toString();
    setGeneratedOtp(mockOtp);

    toast((t) => (
      <span>
        <b>[OTP Code]</b> Your Email Verification OTP is: <b className="text-emerald-600">{mockOtp}</b>
      </span>
    ), { duration: 8000, icon: '🔐' });
  };

  // 2. Signup / Login / OTP Handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isSignup && step === 'FORM') {
        // Enforce password requirements on signup before sending OTP
        if (!hasMinLength || !hasUppercase || !hasNumber || !hasSpecialChar) {
          toast.error("Please meet all password security requirements.");
          setLoading(false);
          return;
        }

        triggerOtpGeneration();
        setStep('OTP_VERIFY');
        setLoading(false);
        return;
      }

      if (isSignup && step === 'OTP_VERIFY') {
        if (emailOtp !== generatedOtp) {
          toast.error("Invalid OTP! Please enter the correct 6-digit code.");
          setLoading(false);
          return;
        }

        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        await setDoc(doc(db, 'users', user.uid), {
          email: email,
          mobile: mobile || 'Not Provided',
          createdAt: new Date().toISOString(),
          role: 'customer'
        });

        toast.success("Email verified & Account created successfully!");
        navigate('/');
        return;
      }

      if (!isSignup) {
        await signInWithEmailAndPassword(auth, email, password);
        toast.success("Logged in successfully!");
        navigate('/');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // 3. Forgot Password Handler
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your registered email address first.");
      return;
    }
    try {
      setLoading(true);
      await sendPasswordResetEmail(auth, email);
      toast.success("Password reset link sent to your email! Please check your inbox.");
      setIsForgotPassword(false);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-emerald-950 to-gray-900 flex items-center justify-center px-4 py-12">
      <div className="bg-white/15 backdrop-blur-xl border border-white/20 p-8 rounded-3xl shadow-2xl max-w-md w-full text-white">
        
        {/* Header / Title */}
        <div className="text-center mb-8">
          <div className="bg-emerald-500 w-12 h-12 rounded-2xl mx-auto flex items-center justify-center shadow-lg shadow-emerald-500/30 mb-3">
            {isForgotPassword ? <KeyRound className="h-6 w-6 text-gray-950" /> : <UserCheck className="h-6 w-6 text-gray-950" />}
          </div>
          <h2 className="text-2xl font-black tracking-tight text-emerald-400">
            {isForgotPassword ? 'Reset Password' : (isSignup ? (step === 'OTP_VERIFY' ? 'Verify Email OTP' : 'Create Account') : 'Welcome Back')}
          </h2>
          <p className="text-xs text-gray-300 mt-1">
            {isForgotPassword 
              ? 'Enter your email to receive a password reset link' 
              : (isSignup 
                  ? (step === 'OTP_VERIFY' ? 'Enter the 6-digit OTP sent for verification' : 'Sign up securely with email verification') 
                  : 'Login with your credentials or Google')}
          </p>
        </div>

        {/* FORGOT PASSWORD VIEW */}
        {isForgotPassword ? (
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-gray-300 mb-1">Registered Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input 
                  type="email" 
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                  required 
                  placeholder="name@example.com"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 pl-10 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-emerald-400"
                />
              </div>
            </div>

            <button 
              type="submit" 
              disabled={loading} 
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-gray-950 font-bold py-3 rounded-xl transition-all shadow-lg shadow-emerald-500/20"
            >
              {loading ? 'Sending Link...' : 'Send Reset Link'}
            </button>

            <button 
              type="button" 
              onClick={() => setIsForgotPassword(false)} 
              className="w-full flex items-center justify-center space-x-1 text-xs text-gray-300 hover:text-emerald-400 mt-2"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              <span>Back to Login</span>
            </button>
          </form>
        ) : (
          /* REGULAR LOGIN / SIGNUP & OTP VIEW */
          <form onSubmit={handleSubmit} className="space-y-4">
            
            {step === 'OTP_VERIFY' ? (
              /* EMAIL OTP VERIFICATION STEP */
              <>
                <div>
                  <label className="block text-xs font-bold text-gray-300 mb-1">Enter 6-Digit Email OTP</label>
                  <input 
                    type="text" 
                    maxLength="6"
                    value={emailOtp} 
                    onChange={(e) => setEmailOtp(e.target.value)} 
                    required 
                    placeholder="123456"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-center text-xl tracking-widest text-white placeholder-gray-500 focus:outline-none focus:border-emerald-400"
                  />
                </div>

                <button 
                  type="submit" 
                  disabled={loading} 
                  className="w-full bg-emerald-500 hover:bg-emerald-400 text-gray-950 font-bold py-3 rounded-xl transition-all shadow-lg shadow-emerald-500/20"
                >
                  {loading ? 'Verifying...' : 'Verify OTP & Complete Signup'}
                </button>

                <div className="flex items-center justify-between mt-3 text-xs">
                  <button 
                    type="button" 
                    onClick={() => setStep('FORM')} 
                    className="text-gray-400 hover:text-white"
                  >
                    Change email
                  </button>
                  <button 
                    type="button" 
                    onClick={triggerOtpGeneration} 
                    className="text-emerald-400 font-semibold hover:underline"
                  >
                    Resend OTP
                  </button>
                </div>
              </>
            ) : (
              /* STANDARD FORM STEP */
              <>
                {isSignup && (
                  <div>
                    <label className="block text-xs font-bold text-gray-300 mb-1">Mobile Number (Optional)</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <input 
                        type="tel" 
                        value={mobile} 
                        onChange={(e) => setMobile(e.target.value)} 
                        maxLength="10"
                        placeholder="9876543210"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 pl-10 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-emerald-400"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-bold text-gray-300 mb-1">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <input 
                      type="email" 
                      value={email} 
                      onChange={(e) => setEmail(e.target.value)} 
                      required 
                      placeholder="name@example.com"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 pl-10 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-emerald-400"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className="block text-xs font-bold text-gray-300">Password</label>
                    {!isSignup && (
                      <button 
                        type="button" 
                        onClick={() => setIsForgotPassword(true)} 
                        className="text-[11px] text-emerald-400 hover:underline"
                      >
                        Forgot Password?
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <input 
                      type={showPassword ? "text" : "password"} 
                      value={password} 
                      onChange={(e) => setPassword(e.target.value)} 
                      required 
                      placeholder="••••••••"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 pl-10 pr-10 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-emerald-400"
                    />
                    <button 
                      type="button" 
                      onClick={() => setShowPassword(!showPassword)} 
                      className="absolute right-3 top-3 text-gray-400 hover:text-white focus:outline-none"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>

                  {/* Password Requirement Checklist (Visible only during Signup) */}
                  {isSignup && (
                    <div className="mt-2.5 bg-black/20 p-2.5 rounded-xl border border-white/5 space-y-1 text-[11px]">
                      <p className="text-gray-400 font-semibold mb-1">Password requirements:</p>
                      <div className="grid grid-cols-2 gap-1">
                        <div className={`flex items-center space-x-1 ${hasMinLength ? 'text-emerald-400' : 'text-gray-500'}`}>
                          {hasMinLength ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                          <span>At least 6 chars</span>
                        </div>
                        <div className={`flex items-center space-x-1 ${hasUppercase ? 'text-emerald-400' : 'text-gray-500'}`}>
                          {hasUppercase ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                          <span>One uppercase letter</span>
                        </div>
                        <div className={`flex items-center space-x-1 ${hasNumber ? 'text-emerald-400' : 'text-gray-500'}`}>
                          {hasNumber ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                          <span>One number</span>
                        </div>
                        <div className={`flex items-center space-x-1 ${hasSpecialChar ? 'text-emerald-400' : 'text-gray-500'}`}>
                          {hasSpecialChar ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                          <span>One special char</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <button 
                  type="submit" 
                  disabled={loading} 
                  className="w-full bg-emerald-500 hover:bg-emerald-400 text-gray-950 font-bold py-3 rounded-xl transition-all shadow-lg shadow-emerald-500/20"
                >
                  {loading ? 'Please wait...' : (isSignup ? 'Send Verification OTP' : 'Login')}
                </button>

                {/* Google Sign-In Option */}
                <div className="relative flex py-2 items-center">
                  <div className="flex-grow border-t border-white/10"></div>
                  <span className="flex-shrink mx-4 text-xs text-gray-400">or</span>
                  <div className="flex-grow border-t border-white/10"></div>
                </div>

                <button 
                  type="button" 
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  className="w-full bg-white/10 hover:bg-white/20 border border-white/10 text-white font-bold py-2.5 rounded-xl transition-all flex items-center justify-center space-x-2 text-xs shadow-md"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24">
                    <path fill="#EA4335" d="M12 5c1.6 0 3 .6 4.1 1.6l3.1-3.1C17.3 1.8 14.8 1 12 1 7.4 1 3.5 3.6 1.6 7.4l3.7 2.9C6.2 7.1 8.9 5 12 5z"/>
                    <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5c-.3 1.5-1.1 2.8-2.4 3.7l3.7 2.9c2.2-2 3.7-5 3.7-8.8z"/>
                    <path fill="#FBBC05" d="M5.3 14.7c-.2-.7-.4-1.5-.4-2.7s.2-2 .4-2.7L1.6 6.4C.6 8.4 0 10.6 0 13s.6 4.6 1.6 6.6l3.7-2.9z"/>
                    <path fill="#34A853" d="M12 23c3.2 0 6-1.1 8-3l-3.7-2.9c-1.1.7-2.5 1.2-4.3 1.2-3.1 0-5.8-2.1-6.7-5.3L1.6 15.9C3.5 19.7 7.4 23 12 23z"/>
                  </svg>
                  <span>Continue with Google</span>
                </button>
              </>
            )}

          </form>
        )}

        {/* Toggle between Login and Signup */}
        {!isForgotPassword && step === 'FORM' && (
          <div className="text-center mt-6">
            <button 
              onClick={() => setIsSignup(!isSignup)} 
              className="text-xs text-emerald-400 font-semibold hover:underline"
            >
              {isSignup ? 'Already have an account? Login here' : "Don't have an account? Sign Up with OTP"}
            </button>
          </div>
        )}

      </div>
    </div>
  );
}